import { Phone, MessageCircle, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PHONE_PRIMARY, PHONE_PRIMARY_TEL, getWhatsAppUrl } from "@/lib/constants";
import { track, trackCall, trackWhatsApp } from "@/lib/analytics";

interface Props {
  eyebrow: string;
  h1: string;
  subheadline: string;
  image: string;
  imageAlt: string;
  whatsappMessage?: string;
  ctaLabel?: string;
}

const SeoHero = ({ eyebrow, h1, subheadline, image, imageAlt, whatsappMessage, ctaLabel }: Props) => (
  <section className="bg-background py-7 md:py-16 border-b border-border">
    <div className="container">
      <div className="grid min-w-0 lg:grid-cols-[1.05fr_1fr] gap-6 lg:gap-x-14 lg:gap-y-7 items-center">
        <div className="min-w-0 lg:self-end">
          <span className="text-eyebrow text-primary mb-3 block">{eyebrow}</span>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-[2.75rem] font-light tracking-normal text-primary leading-[1.2] mb-4 break-words">
            {h1}
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
            {subheadline}
          </p>
        </div>
        <div className="min-w-0 order-3 lg:order-none lg:col-start-1 lg:row-start-2 lg:self-start">
          <div className="grid grid-cols-2 gap-3 lg:flex lg:flex-wrap">
            <Button asChild variant="gold" size="lg" className="col-span-2 w-full lg:w-auto h-auto min-h-12 py-3 px-4 whitespace-normal tracking-normal text-center">
              <a href="#book" onClick={() => track("book_appointment_click", { source: "service_hero" })}>
                <CalendarDays className="h-4 w-4" />
                <span className="lg:hidden">Book My Consultation</span>
                <span className="hidden lg:inline">{ctaLabel || "Book My Consultation"}</span>
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full lg:w-auto px-3 tracking-normal">
              <a href={getWhatsAppUrl(whatsappMessage)} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("service_hero")}>
                <MessageCircle className="h-4 w-4" /> WhatsApp Us
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full lg:w-auto px-3 tracking-normal">
              <a href={PHONE_PRIMARY_TEL} onClick={() => trackCall("service_hero")} aria-label={`Call ${PHONE_PRIMARY}`}><Phone className="h-4 w-4" /><span className="lg:hidden">Call Clinic</span><span className="hidden lg:inline">{PHONE_PRIMARY}</span></a>
            </Button>
          </div>
        </div>
        <div className="relative min-w-0 lg:col-start-2 lg:row-start-1 lg:row-span-2">
          <div className="aspect-[4/3] lg:aspect-[5/4] max-h-[520px] rounded-lg overflow-hidden bg-muted">
            <img
              src={image}
              alt={imageAlt}
              className="w-full h-full object-cover object-center"
              loading="eager"
              fetchPriority="high"
              width={1200}
              height={900}
            />
          </div>
          <div className="absolute -bottom-4 -left-4 bg-card border border-gold/40 rounded-xl px-4 py-3 shadow-xl hidden md:block">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Mutha's Advanced Dental Care Kalyan</p>
            <p className="font-heading text-sm font-medium">15+ years · 10,000+ smiles</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default SeoHero;